import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Form,
  Input,
  message,
  Progress,
  UploadFile,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import Dragger from "antd/es/upload/Dragger";
import DaggerCard from "@/components/Cards/DaggerCard/DaggerCard";
import { toast } from "sonner";
import { RcFile, UploadProps } from "antd/es/upload";
import { green, red } from "@ant-design/colors";
import { customUploadRequest } from "@/util/axiosUpload";
import { AddressAutofill } from "@mapbox/search-js-react";
import { MAPBOX_TOKEN } from "@/config/urls";

interface CompanyAddressProps {
  onFormFinish?: (values: ICreateResponderCompanyDto) => void;
  initialValues?: any;
  onNext?: () => void;
  onPrevious?: () => void;
  isLastStep?: boolean;
}

const CompanyAddress = ({
  initialValues,
  onNext,
  onPrevious,
  isLastStep,
  onFormFinish,
}: CompanyAddressProps) => {
  const [form] = Form.useForm();
  const [isUploading, setIsUploading] = useState(false);
  const [pdfUrl, setPDFUrl] = useState("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [addressInputValue, setAddressInputValue] = useState("");

  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      const fieldsToSet: any = {};
      if (initialValues.companyAddress !== undefined) {
        fieldsToSet.companyAddress = initialValues.companyAddress;
        setAddressInputValue(initialValues.companyAddress);
      }
      if (initialValues.proofofAddressUrl !== undefined) {
        fieldsToSet.proofofAddressUrl = initialValues.proofofAddressUrl;

        if (initialValues.proofofAddressUrl) {
          if (
            !fileList.length ||
            (fileList.length &&
              fileList[0]?.url !== initialValues.proofofAddressUrl)
          ) {
            let fileName =
              initialValues.proofofAddressUrl.split("/").pop() ||
              "proof-of-address.pdf";

            // Truncate filename if longer than 20 characters
            if (fileName.length > 20) {
              fileName = fileName.substring(0, 20) + "qwertyuiopasdfg... .pdf";
            }

            setFileList([
              {
                uid: initialValues.proofofAddressUrl,
                name: fileName,
                status: "done",
                url: initialValues.proofofAddressUrl,
              },
            ]);
          }
        } else {
          setFileList([]);
        }
      } else {
        fieldsToSet.proofofAddressUrl = undefined;
        setFileList([]);
      }
      form.setFieldsValue(fieldsToSet);
    } else {
      form.resetFields();
      setFileList([]);
      setAddressInputValue("");
    }
  }, [initialValues, form]);

  const handleAddressRetrieve = (res: any) => {
    try {
      console.log("Mapbox company address response:", res);

      const feature = res.features?.[0];
      if (!feature) {
        console.warn("No features found in Mapbox response");
        toast.error("No address found. Please try a different search.");
        return;
      }

      // Extract the full address
      const fullAddress =
        feature.place_name ||
        feature.properties?.full_address ||
        [feature.text, feature.address].filter(Boolean).join(" ") ||
        "";

      if (!fullAddress) {
        console.warn("Could not extract address from feature:", feature);
        toast.error("Address format not recognized. Please select another suggestion.");
        return;
      }

      console.log("Selected company address:", fullAddress);

      // Set the address in the form field and input state
      form.setFieldsValue({ companyAddress: fullAddress });
      setAddressInputValue(fullAddress);
    } catch (error) {
      console.error("Company address processing error:", error);
      toast.error("Error processing address. Please try again.");
    }
  };

  const onFinish = (values: ICreateResponderCompanyDto) => {
    toast.success("Saved successfully");
    if (onFormFinish) {
      onFormFinish(values);
      console.log("company address", values);
    }
    form.resetFields();
    setFileList([]);
    setPDFUrl("");
    setAddressInputValue("");
  };

  const beforeUpload = (file: RcFile) => {
    const isPdf = ["application/pdf"].includes(file.type);
    // const isLt2M = file.size / 1024 / 1024 < 10;
    if (!isPdf) {
      toast.error("You can only upload PDF file!");
      return false; // This prevents the file from being added to the list
    }
    return isPdf;
  };

  const customUpload = async (options: any) => {
    const { onSuccess, onError, file, onProgress } = options;

    // Truncate filename if longer than 20 characters
    let fileToUpload = file;
    if (file.name.length > 20) {
      const truncatedName = file.name.substring(0, 20) + "qwertyuiopasdfg... .pdf";
      fileToUpload = new File([file], truncatedName, { type: file.type });
    }

    const formData = new FormData();
    formData.append("file", fileToUpload);

    try {
      const response = await API.post("/util/post/document", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (event) => {
          if (event.total) {
            const percent = Math.floor((event.loaded / event.total) * 100);
            onProgress({ percent });
          }
        },
      });

      onSuccess(response.data, file);
    } catch (error) {
      console.error("Upload error:", error);
      onError(error);
    }
  };

  const props: UploadProps = {
    name: "file",
    customRequest: customUploadRequest,
    fileList: fileList, // Controlled component
    maxCount: 1,
    beforeUpload: beforeUpload,
    onChange(info) {
      let newFileList = [...info.fileList];
      newFileList = newFileList.slice(-1);

      // Truncate filename if longer than 20 characters for display
      newFileList = newFileList.map((file) => {
        if (file.name && file.name.length > 20) {
          return {
            ...file,
            name: file.name.substring(0, 20) + "qwertyuiopasdfg... .pdf",
          };
        }
        return file;
      });

      setFileList(newFileList);

      if (info.file.status === "done") {
        message.success(`${info.file.name} file uploaded successfully`);
        form.setFieldsValue({
          proofofAddressUrl: info.file.response.path,
        });
      } else if (info.file.status === "error") {
        message.error(`${info.file.name} file upload failed.`);
        form.setFieldsValue({ proofofAddressUrl: undefined });
        setFileList([]);
      }
    },
    onRemove: () => {
      form.setFieldsValue({ proofofAddressUrl: undefined });
      setFileList([]);
      return true;
    },
  };

  return (
    <div className="flex w-full h-full">
      <Form form={form} onFinish={onFinish} layout="vertical">
        <div className="flex flex-col w-full">
          <p className="text-sm">Step 4: Company Address</p>
          <Progress
            steps={6}
            size={[80, 10]}
            strokeColor={["#1757a2", "#1757a2", "#1757a2", "#1757a2"]}
            percent={60}
            showInfo={false}
            className="rounded-full"
            strokeLinecap="round"
            strokeWidth={1}
            style={{
              height: "6px",
              borderRadius: "999px",
            }}
          />
        </div>

        <div className="flex flex-col w-full mt-3">
          <p className="text-sm">Company Address</p>

          {/* Hidden input for form validation */}
          <Form.Item
            name="companyAddress"
            rules={[
              { required: true, message: "Please enter a complete address (e.g., 123 West Street, City) and select from the suggestions" },
            ]}
            noStyle
          >
            <Input type="hidden" />
          </Form.Item>

          {/* Visible native input with AddressAutofill */}
          <AddressAutofill
            accessToken={MAPBOX_TOKEN}
            browserAutofillEnabled={true}
            onRetrieve={handleAddressRetrieve}
          >
            <input
              type="text"
              autoComplete="address-line1"
              placeholder="Enter complete address (e.g., 123 West Street, City) and select from suggestions"
              value={addressInputValue}
              onChange={(e) => {
                setAddressInputValue(e.target.value);
                form.setFieldsValue({ companyAddress: e.target.value });
              }}
              className="w-full px-3 py-2 mb-5 border border-gray-300 rounded-md shadow-sm hover:border-blue-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-colors text-base"
              style={{
                height: '40px',
                fontSize: '14px',
              }}
            />
          </AddressAutofill>
        </div>
        <div className="flex flex-col w-full">
          <p className="text-sm">Proof of Address</p>
          <Form.Item
            name="proofofAddressUrl"
            className="mb-5 col-12"
            rules={[
              {
                required: true,
                message: "Please attach the proof of address.",
              },
            ]}
          >
            <div className="flex w-full gap-6 flx-row justify-content-start">
              <Dragger
                {...props}
                beforeUpload={beforeUpload}
                className="w-full"
                style={{ background: "transparent" }}
                maxCount={1}
                accept=".pdf"
              >
                <DaggerCard
                  description="Please upload a pdf file type."
                  header="Drop your proof of address here or "
                />
              </Dragger>
            </div>
          </Form.Item>
        </div>
        <div className="flex w-full">
          <Form.Item className="w-12 pt-2 mb-1">
            <div className="flex w-full gap-2 col-12 justify-content-end align-content-center">
              <Button
                type="primary"
                ghost
                onClick={onPrevious}
                size="large"
                className="w-3"
              >
                Back
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                className="w-3 bg-[#1757A2]"
              >
                Next
              </Button>
            </div>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default CompanyAddress;
